// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.EmailPopup.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import {
  DELIVERY_OPTIONS,
  DELIVERY_METHOD,
  ATTACH_TYPE_OPTIONS,
  REQUIRE_CONSTRAINTS,
  EMAIL_SUBJECT,
  EMAIL_BODY
} from 'logic/data/Izenda.EmailConstants';

import localizer from 'share/localization/Izenda.Localizer';
import ReactDOM from 'react-dom';
import React, { Component } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import actions from './Izenda.EmailActions';
import SystemVariablesUI from 'logic/models/Izenda.SystemVariablesUI';
import SubscribedInstancesStore from 'logic/Izenda.SubscribedInstancesStore';
import { EMAIL_TYPE } from 'logic/data/Izenda.EmailConstants';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import EmailProgressBar from './Izenda.EmailProgressBar';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import EmailInput from './Izenda.EmailInput';
import DropDown from 'share/components/controls/Izenda.DropDown';
import IconAfterCursorInput from 'share/components/controls/Izenda.IconAfterCursorInput';
import Validator from 'share/components/hoc/Izenda.Validator';
import SystemVariables from 'modules/Subscription/Izenda.SystemVariables';
import { Popover } from 'share/components/controls/Izenda.Popover';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import { filter, get, getOr, find, isEmpty, head, omit } from 'lodash/fp';
import FilterValueSelection from 'modules/Subscription/Izenda.FilterValueSelection';
const IconAfterCursorInputValidator = Validator()(IconAfterCursorInput);

function EmailInputOption(props: any) {
  const { label, options, value } = props;
  const ValidatableDropDown = Validator()(DropDown);
  return (
    <GridLayout.Row>
      <GridLayout.Col cols={2} className='Izenda-EmailPopup-Input'>
        <span>{label}</span>
      </GridLayout.Col>
      <GridLayout.Col cols={10}>
        <ValidatableDropDown field={label} options={options} value={value} onChange={props.onChange} validations={REQUIRE_CONSTRAINTS} />
      </GridLayout.Col>
    </GridLayout.Row>
  );
}

@connect(actions)
export default class EmailPopup extends Component {
  constructor(props: any) {
    super(props);
    this.systemVariablesUI = new SystemVariablesUI();
    this.subscribedInstancesStore = new SubscribedInstancesStore();

    const {
      email,
      type,
      dashboardId,
      reportId,
      authorization,
      authorizationConstants: { PERMISSION_KEY }
    } = this.props;
    this.domNodes = {};

    type === EMAIL_TYPE.dashboard
      ? this.subscribedInstancesStore.updateDashboardId(dashboardId)
      : this.subscribedInstancesStore.updateReportId(reportId);

    // Get list of delivery method that user can use
    this.deliveryOptions = filter(
      option => authorization.checkPrivileges([PERMISSION_KEY[option.key]]),
      DELIVERY_OPTIONS
    );

    if(find({ value: DELIVERY_METHOD.attachment }, this.deliveryOptions)){
      // Get list of attachment options that user can use
      this.attachmentOptions = filter(
        option => authorization.checkPrivileges([PERMISSION_KEY[option.key]]),
        ATTACH_TYPE_OPTIONS
      );

      if(this.attachmentOptions.length > 0){
        if(!find({ value: email.attachmentType }, this.attachmentOptions)){
          email.attachmentType = getOr('', '[0].value', this.attachmentOptions);
        }
      }else{
        this.deliveryOptions = filter(option => option.value !== DELIVERY_METHOD.attachment, this.deliveryOptions);
      }
    }

    if(!find({ value: email.deliveryMethod }, this.deliveryOptions)){
      email.deliveryMethod = getOr('', '[0].value', this.attachmentOptions);
    }
  }

  async componentDidMount() {
    const { type } = this.props;
    this.emailTemplates = await this.props.getEmailTemplates(type);
    if (!isEmpty(this.deliveryOptions)) {
      this.setDeliveryMethod(head(this.deliveryOptions));
    }
  }

  setDeliveryMethod = (value: any) => {
    const emailBody = this.findTemplateMatchWithDeliveryMethod(value.value);
    this.props.setDeliveryMethod(emailBody, value.value);
  };

  /**
   * Find email body template for delivery method
   * @param {string} value
   */
  findTemplateMatchWithDeliveryMethod(value: string) {
    return get('value', find(template => template.key.toLowerCase() === value.toLowerCase(), this.emailTemplates));
  }

  showSystemVariablesPopup(sourceType: string, position: any) {
    const domNode = ReactDOM.findDOMNode(this.domNodes[sourceType]);
    if (!domNode) return;

    const {
      offsetParent: { offsetLeft, offsetTop }
    } = domNode;

    const left = offsetLeft + (position && position.left + 20);
    const top = offsetTop + (position && position.top + 20);

    const $sourceNode = sourceType === EMAIL_SUBJECT.KEY ? $izenda(EMAIL_SUBJECT.CLASS) : $izenda(EMAIL_BODY.CLASS);

    const cursorPosition = $izenda($sourceNode).prop('selectionStart');
    const currentVisible = this.systemVariablesUI.visible;

    this.systemVariablesUI.populateData({
      visible: !currentVisible,
      left: left,
      top: top,
      sourceType: sourceType,
      cursorPosition: cursorPosition,
      $sourceNode: $sourceNode
    });
  }

  /**
   * Handle when select system variable
   * @param {*} name
   */
  onSelectSystemVariable = (name: string) => {
    const props = this.props;
    const systemVariablesUI = this.systemVariablesUI;
    const {
      email: { subject, body }
    } = props;

    const sourceValue = systemVariablesUI.sourceType === EMAIL_SUBJECT.KEY ? subject : body;
    const updateFn = systemVariablesUI.sourceType === EMAIL_SUBJECT.KEY ? props.setSubject : props.setEmailBody;

    const cursorPos = systemVariablesUI.cursorPosition;
    const textBefore = sourceValue.substring(0, cursorPos);
    const textAfter = sourceValue.substring(cursorPos, sourceValue.length);
    updateFn({ target: { value: `${textBefore}${name}${textAfter}` } });

    systemVariablesUI.updateVisible(false);
    systemVariablesUI.$sourceNode.focus();
  };

  showSubjectSystemVariables = (position: any) => {
    this.showSystemVariablesPopup(EMAIL_SUBJECT.KEY, position);
  };

  showBodySystemVariables = (position: any) => {
    this.showSystemVariablesPopup(EMAIL_BODY.KEY, position);
  };

  setDomNodeSubject = (dom: any) => {
    this.domNodes[EMAIL_SUBJECT.KEY] = dom;
  };

  setDomNodeBody = (dom: any) => {
    this.domNodes[EMAIL_BODY.KEY] = dom;
  };

  /**
   * Render system variable popover
   */
  renderSystemVariablesPopover() {
    return (
      <Popover
        visible={this.systemVariablesUI.visible}
        positionLeft={this.systemVariablesUI.left}
        positionTop={this.systemVariablesUI.top}
      >
        <SystemVariables store={this.subscribedInstancesStore} onSelect={this.onSelectSystemVariable} />
      </Popover>
    );
  }

  renderEmailContainer() {
    const props = this.props;
    const {
      email,
      email: { to, cc, subject, deliveryMethod, body }
    } = props;

    return (
      <GridLayout>
        <EmailInput
          multi
          label={localizer.getText('EMAIL_LABEL_TO')}
          value={to}
          onChange={props.setToAddress}
          addNewEmail={props.addNewEmail}
          addRecipientsFromPopup={props.addToEmailFromPopup}
          email={email}
          isRequired
        />
        <EmailInput
          multi
          label={localizer.getText('EMAIL_LABEL_CC')}
          value={cc}
          onChange={props.setCcAddress}
          addRecipientsFromPopup={props.addCcEmailFromPopup}
          email={email}
          addNewEmail={props.addNewEmail}
        />
        <GridLayout.Row>
          <GridLayout.Col cols={2} className='Izenda-EmailPopup-Input'>
            <span>{localizer.getText('EMAIL_LABEL_SUBJECT')}</span>
          </GridLayout.Col>
          <GridLayout.Col cols={10}>
            <IconAfterCursorInputValidator
              ref={this.setDomNodeSubject}
              type='input'
              className='form-control email-subject'
              value={subject}
              onChange={props.setSubject}
              onClickIcon={this.showSubjectSystemVariables}
              field={EMAIL_SUBJECT.KEY}
              validations={REQUIRE_CONSTRAINTS}
            />
          </GridLayout.Col>
        </GridLayout.Row>
        <EmailInputOption
          label={localizer.getText('EMAIL_LABEL_METHOD')}
          options={this.deliveryOptions}
          value={deliveryMethod}
          onChange={this.setDeliveryMethod}
        />
        {this.renderAttachment()}
        <GridLayout.Row>
          <GridLayout.Col cols={2}>
            <span>{localizer.getText('EMAIL_LABEL_EMAIL_BODY')}</span>
          </GridLayout.Col>
          <GridLayout.Col cols={10}>
            <IconAfterCursorInputValidator
              ref={this.setDomNodeBody}
              className='form-control email-body'
              rows='8'
              value={body}
              onChange={props.setEmailBody}
              field={EMAIL_BODY.KEY}
              onClickIcon={this.showBodySystemVariables}
              validations={REQUIRE_CONSTRAINTS}
            />
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );
  }

  renderAttachment() {
    const { props: { email: { deliveryMethod, attachmentType }, setAttachment }, attachmentOptions } = this;

    if (deliveryMethod !== DELIVERY_METHOD.attachment) return null;

    return (
      <EmailInputOption
        label={localizer.getText('EMAIL_LABEL_ATTACHMENT')}
        options={attachmentOptions}
        value={attachmentType}
        onChange={setAttachment}
      />
    );
  }

  /**
   * Render attachment dropdown
   */
  renderFilters() {
    const { props } = this;
    return (
      <GridLayout>
        <GridLayout.Row>
          <GridLayout.Col cols={2} />
          <GridLayout.Col cols={10}>
            <FilterValueSelection {...omit('localization', props)} />
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );
  }

  render() {
    const props = this.props;
    return (
      <Popup visible onClosing={props.onClosing} type='none' autoClose={false} sizeClass='modal-lg'>
        <Popup.Header title={localizer.getText('EMAIL_TOOLTIP_COMPOSE_EMAIL')} />
        <Popup.Body>
          <div className='Izenda-EmailPopup-EmailContainer'>
            <ScrollBox>
              <div className='Izenda-EmailPopup-EmailBody'>
                {this.renderEmailContainer()}
                {this.renderSystemVariablesPopover()}
                <hr />
                <div className='Izenda-EmailPopup-Filters'>{this.renderFilters()}</div>
              </div>
            </ScrollBox>
          </div>
          <EmailProgressBar setLoadingPromise={props.setLoadingPromise} promiseFunction={props.loadingPromise} />
        </Popup.Body>

        <Popup.Footer>
          <Popup.ConfirmationButton key='accept' type='accept'>
            {localizer.getText('COMMON_LABEL_SEND')}
          </Popup.ConfirmationButton>
          <Popup.ConfirmationButton key='cancel' type='cancel'>
            {localizer.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/CommonPopup/Izenda.EmailPopup.js